import { useMemo } from 'react';
import { Channel } from '@src/models/channel';
import { getPlanLabel } from '../constants';
import type { ApplicationMemberResponseModel } from '@uniquegood/realworld-admin-interface';
import type { ChannelDetailWithMembers } from '@src/hooks/channel/queries/use-channel-detail';
import { formatPhoneNumberForDisplay } from '@src/utils/phone';

interface ChannelInfoTableProps {
  channel: ChannelDetailWithMembers | Channel;
  showPlan?: boolean; // 요금제 정보 표시 여부
}

interface InfoRowProps {
  label: string;
  value: string | React.ReactNode;
  isLast?: boolean;
}

function InfoRow({ label, value, isLast = false }: InfoRowProps) {
  return (
    <div className={`flex h-10 ${!isLast ? 'border-b border-[#F0F0F0]' : ''}`}>
      <div className="bg-[#F5F5F5] w-[120px] flex items-center px-4 py-3">
        <span className="text-[14px] font-bold text-[#101010] leading-[16px]">{label}</span>
      </div>
      <div className="flex-1 px-4 py-0 flex items-center">
        {typeof value === 'string' ? (
          <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
            {value}
          </span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

// 비즈니스 유형 레이블 매핑
const businessTypeLabels: Record<string, string> = {
  none: '해당 없음',
  company: '일반 기업',
  agency: '이벤트∙광고대행사',
  k12: '초∙중∙고등학교',
  university: '대학교∙대학원',
  public: '공공기관∙지자체∙비영리단체',
  library: '도서관∙과학관∙박물관',
};

/**
 * 채널 정보를 표시하는 공통 테이블 컴포넌트
 * plan-no-payment-form-modal, payment-request-form-modal에서 사용
 */
export function ChannelInfoTable({ channel }: ChannelInfoTableProps) {
  // 비즈니스 유형 레이블 가져오기
  const businessTypeLabel = channel.businessType 
    ? businessTypeLabels[channel.businessType] || channel.businessType
    : '-';

  // 소유자 정보 추출 (members에서 Owner만 필터링)
  const owners = useMemo(() => {
    const channelWithMembers = channel as ChannelDetailWithMembers;
    if (!channelWithMembers.members || channelWithMembers.members.length === 0) {
      return [];
    }
    return channelWithMembers.members
      .filter((member: ApplicationMemberResponseModel) => member.ownershipLevel === 'Owner')
      .map((member: ApplicationMemberResponseModel) => member.name || member.id || '-');
  }, [(channel as ChannelDetailWithMembers).members]);

  // 소유자 표시 텍스트
  const ownersText = owners.length > 0 ? owners.join(', ') : '-';

  return (
    <div className="flex flex-col gap-6 w-[520px]">
      {/* 채널 기본 정보 */}
      <div className="border border-[#F0F0F0] flex flex-col">
        <InfoRow label="채널명" value={channel.channelName || '-'} />
        <InfoRow label="채널 ID" value={channel.channelId} />
        <InfoRow 
          label="소유자" 
          value={ownersText}
        />
          <InfoRow 
            label="요금제" 
            value={getPlanLabel(channel.plan)} 
            isLast 
          />
      </div>

      {/* 고객 정보 */}
      <div className="border border-[#F0F0F0] flex flex-col">
        <InfoRow 
          label="단체명" 
          value={
            <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] overflow-hidden text-ellipsis whitespace-nowrap">
              {channel.customerName || '-'}
            </span>
          }
        />
        <InfoRow 
          label="담당자" 
          value={channel.contactName || '-'}
        />
        <InfoRow label="이메일" value={channel.contactEmail || '-'} />
        <InfoRow 
          label="비즈니스 유형" 
          value={
            <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] overflow-hidden text-ellipsis whitespace-nowrap">
              {businessTypeLabel}
            </span>
          }
        />
        <InfoRow label="직책" value={channel.contactTitle || '-'} />
        <InfoRow label="연락처" value={formatPhoneNumberForDisplay(channel.contactPhone)} isLast />
      </div>
    </div>
  );
}

